import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/service/actionCreator";
import {
  ExportAbsenceAuditLogs,
  ExportNotificationAuditLogs,
  GetAbsenceAuditLogs,
  GetNotificationAuditLogs,
} from "@modules/service/constants/actions";
import { OPERATORS, transformSearchQuery } from "@transformers/request";
import { downloadCsvFile } from "@sagas/utils";
import { sendSuccessMessage } from "@modules/app/actionCreator";

// READ
const AbsenceAuditRewrites = {
  dateFrom: {
    name: "revision_date",
    operator: OPERATORS.GREATER_THAN,
  },
  dateTo: {
    name: "revision_date",
    operator: OPERATORS.LESS_THAN,
  },
  userId: {
    name: "user_id",
    operator: OPERATORS.EQUALS,
  },
  employeeId: {
    name: "employee_id",
    operator: OPERATORS.EQUALS,
  },
};

const NotificationAuditRewrites = {
  dateFrom: {
    name: "activity_date",
    operator: OPERATORS.GREATER_THAN,
  },
  dateTo: {
    name: "activity_date",
    operator: OPERATORS.LESS_THAN,
  },
  userId: {
    name: "user_id",
    operator: OPERATORS.EQUALS,
  },
  employeeId: {
    name: "target_employee_id",
    operator: OPERATORS.EQUALS,
  },
};

type Request = {
  clientId: number,
  search: Object,
  page?: number,
};

export function* onGetAbsenceAuditLogs({ payload }: Action<Request>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.search) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/client-admin/client/${payload.clientId}/audit/absence-record`,
      {
        params: {
          search: transformSearchQuery(payload.search, AbsenceAuditRewrites),
          page: payload.page || 1,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetAbsenceAuditLogsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceAuditLogsError(e.response));
  }
}

function* onExportAbsenceAuditLogs({ payload }: Action<Request>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.search) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/client-admin/client/${payload.clientId}/audit/absence-record/export`,
      {
        params: {
          search: transformSearchQuery(payload.search, AbsenceAuditRewrites),
        },
      },
    );

    downloadCsvFile(response.data, `Absence_Audit_Logs-${payload.clientId}.csv`);

    yield put(actionCreators.ExportAbsenceAuditLogsSuccess(response.data));
    yield put(sendSuccessMessage("ABSENCE_AUDITS_EXPORTED"));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceAuditLogsError(e.response));
  }
}

export function* onGetNotificationAuditLogs({ payload }: Action<Request>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.search) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/client-admin/client/${payload.clientId}/audit/notification-contacts`,
      {
        params: {
          search: transformSearchQuery(payload.search, NotificationAuditRewrites),
          page: payload.page || 1,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetNotificationAuditLogsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNotificationAuditLogsError(e.response));
  }
}

function* onExportNotificationAuditLogs({ payload }: Action<Request>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.search) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/client-admin/client/${payload.clientId}/audit/notification-contacts/export`,
      {
        params: {
          search: transformSearchQuery(payload.search, NotificationAuditRewrites),
        },
      },
    );

    downloadCsvFile(response.data, `Notification_Audit_Logs-${payload.clientId}.csv`);

    yield put(actionCreators.ExportNotificationAuditLogsSuccess(response.data));
    yield put(sendSuccessMessage("NOTIFICATION_AUDITS_EXPORTED"));
  } catch (e) {
    yield put(actionCreators.ExportNotificationAuditLogsError(e.response));
  }
}

// ROOT
export function* AuditLogsSaga(): Generator<*, void, void> {
  yield all([takeEvery(GetAbsenceAuditLogs, onGetAbsenceAuditLogs)]);
  yield all([takeEvery(ExportAbsenceAuditLogs, onExportAbsenceAuditLogs)]);
  yield all([takeEvery(GetNotificationAuditLogs, onGetNotificationAuditLogs)]);
  yield all([takeEvery(ExportNotificationAuditLogs, onExportNotificationAuditLogs)]);
}

export default AuditLogsSaga;
